import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Pitch } from './types';
import { artistsApi } from '../../repositories/artists';
import { formatDisplayDate } from '../../DashboardPage/utils/dates';
import { ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import {
  XMarkIcon, FolderIcon
} from "@heroicons/react/24/outline";
import Swal from 'sweetalert2';

interface ArtistPitchesTabProps {
  artistId: string;
}

interface ExtendedPitch extends Pitch {
  releaseDate: string;
  userId: number;
  trackISRC: string;
  mainGenre?: string;
  configuration?: string;
}

const ArtistPitchesTab: React.FC<ArtistPitchesTabProps> = ({ artistId }) => {
  const [pitches, setPitches] = useState<ExtendedPitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [copiedFields, setCopiedFields] = useState<{ [key: string]: boolean }>({});
  const pageSize = 10;
  // Search 
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"artistName" | "releaseTitle">("artistName");

  useEffect(() => {
    loadPitches();
  }, [artistId, currentPage]);

  const loadPitches = async () => {
    try {
      setLoading(true);
      const response = await artistsApi.getPitches(artistId, {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        searchType: searchType
      });

      const data = response.data;
      const pitchesData = data.data || data.pitches || [];

      // Map backend response to frontend format
      const mappedPitches: ExtendedPitch[] = pitchesData.map((pitch: any) => ({
        id: pitch.id,
        artistName: pitch.artistName,
        songTitle: pitch.songTitle,
        releaseTitle: pitch.releaseTitle || pitch.songTitle,
        releaseDate: pitch.releaseDate,
        userId: pitch.userId,
        trackISRC: pitch.trackISRC,
        mainGenre: pitch.mainGenre,
        configuration: pitch.configuration,
        status: pitch.status,
        submittedAt: pitch.submittedAt ? new Date(pitch.submittedAt) : undefined,
        createdAt: new Date(pitch.createdAt),
      }));

      // Sort by release date (newest first)
      const sortedPitches = mappedPitches.sort(
        (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
      );

      setPitches(sortedPitches);
      setTotalPages(Math.ceil((data.total || data.totalCount || mappedPitches.length) / pageSize));

    } catch (error: any) {
      console.error('Error loading pitches:', error);

      // Check if it's a 404 (no pitches found) - this is normal
      if (error.response?.status === 404) {
        setPitches([]);
        setTotalPages(1);
      } else {
        // Show error for other types of errors
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to load pitches. Please try again.';

        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Add search handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchType(e.target.value as "artistName" | "releaseTitle");
  };

  const clearSearch = () => {
    setSearchTerm("");

  };
  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadPitches();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchType]);


  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const copyToClipboard = async (text: string, fieldKey: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFields((prev) => ({ ...prev, [fieldKey]: true }));
      setTimeout(() => {
        setCopiedFields((prev) => ({ ...prev, [fieldKey]: false }));
      }, 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Always show search section */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative">
          <select
            value={searchType}
            onChange={handleSearchTypeChange}
            className="px-3 py-2 pr-8 text-sm bg-white border border-gray-300 appearance-none rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="artistName">Artist Name</option>
            <option value="releaseTitle">Release Name</option>
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div className="relative flex-1 sm:w-64">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={`Search by ${searchType === "artistName" ? "artist name" : searchType === "releaseTitle" ? "release name" : "company name"}...`}
            className="block w-full py-2 pl-10 pr-10 text-sm placeholder-gray-500 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Search Results Info */}
      {searchTerm && (
        <div className="p-3 border border-blue-200 rounded-md bg-blue-50">
          <span className="text-sm text-blue-800">
            Found {pitches.length} result{pitches.length !== 1 ? 's' : ''} for "{searchTerm}" in {searchType === "artistName" ? "artist name" : searchType === "releaseTitle" ? "release name" : "company name"}.
          </span>
          {pitches.length > 0 && (
            <button
              onClick={clearSearch}
              className="ml-2 text-sm text-blue-600 underline hover:text-blue-800">
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Conditional content based on assets and search */}
      {pitches.length === 0 && searchTerm ? (

        // No search results found
        <div className="py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <p className="text-gray-500">
            No pitches found matching "{searchTerm}" in {searchType === "artistName" ? "artist name" : searchType === "releaseTitle" ? "release name" : "company name"}.
          </p>
          <button
            onClick={clearSearch}
            className="mt-4 px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
          >
            Clear search to see all assets
          </button>
        </div>
      ) : (

        <div className="overflow-hidden bg-white rounded-lg shadow">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Artist
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Release
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase whitespace-nowrap"
                  >
                    Release Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Genre
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    Configuration
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                  >
                    ISRC
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-4 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pitches.map((pitch, index) => (
                  <tr
                    key={pitch.id}
                    className={`transition-colors hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-25"
                      }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pitch.artistName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="max-w-xs text-sm text-gray-900">
                        <div
                          className="font-medium truncate"
                          title={pitch.releaseTitle}
                        >
                          {pitch.releaseTitle}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDisplayDate(pitch.releaseDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1 flex-nowrap">
                        {pitch.mainGenre ? (
                          pitch.mainGenre
                            .split(",")
                            .map((genre, genreIndex) => (
                              <span
                                key={genreIndex}
                                className="inline-flex px-2 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full"
                              >
                                {genre.trim()}
                              </span>
                            ))
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded-full">
                            Not specified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${pitch.configuration
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-500"
                          }`}
                      >
                        {pitch.configuration || "Not specified"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {pitch.trackISRC ? (
                        <div className="flex items-center">
                          <div
                            className="text-sm text-gray-900 font-mono max-w-[120px] truncate"
                            title={pitch.trackISRC}
                          >
                            {truncateText(pitch.trackISRC, 12)}
                          </div>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                pitch.trackISRC,
                                `isrc-${pitch.id}`
                              )
                            }
                            className="p-1 ml-2 text-gray-400 transition-colors rounded hover:text-blue-600"
                            title="Copy ISRC"
                          >
                            {copiedFields[`isrc-${pitch.id}`] ? (
                              <Check size={16} className="text-green-600" />
                            ) : (
                              <Copy size={16} />
                            )}
                          </button>
                        </div>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded-full">
                          No ISRC
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center space-x-2">
                        <Link to={`/pitch/submission/${pitch.id}`}>
                          <button className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-amber-500 rounded-md hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
                            Review Pitch
                          </button>
                        </Link>
                        {pitch.trackISRC && (
                          <Link to={`/analytics/${pitch.id}`}>
                            <button className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-teal-500 rounded-md hover:bg-teal-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                              Analytics
                            </button>
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {/* Fill empty rows to maintain consistent height */}
                {pitches.length < pageSize &&
                  [...Array(pageSize - pitches.length)].map((_, index) => (
                    <tr key={`empty-${index}`} className="h-16">
                      <td colSpan={7} className="px-6 py-4"></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center text-sm text-gray-700">
              <p className="hidden sm:block">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, pitches.length)}
                </span>{" "}
                of <span className="font-medium">{pitches.length}</span> results
              </p>
              <p className="sm:hidden">
                Page {currentPage} of {totalPages}
              </p>
            </div>

            <div className="flex items-center space-x-1">
              {/* First Page */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                <ChevronLeft size={16} className="mr-0.5" />
                <ChevronLeft size={16} />
              </button>

              {/* Previous Page */}
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>

              {/* Page Numbers */}
              <div className="items-center hidden md:flex">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  const isCurrentPage = pageNum === currentPage;

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium transition-colors focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isCurrentPage
                        ? "z-10 bg-blue-600 border-blue-600 text-white hover:bg-blue-700"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {totalPages > 5 && (
                  <>
                    <span className="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 bg-white border border-gray-300">
                      ...
                    </span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              {/* Next Page */}
              <button
                onClick={() =>
                  setCurrentPage((prev) =>
                    prev < totalPages ? prev + 1 : prev
                  )
                }
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>

              {/* Last Page */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium text-gray-500 transition-colors bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                <ChevronRight size={16} />
                <ChevronRight size={16} className="ml-0.5" />
              </button>
            </div>
          </div>
        </div>)}
    </div>
  );
};

export default ArtistPitchesTab;